import React, { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { useAssignUnderwriter } from "@/hooks/queries";
import { underwriterService } from "@/services/underwriters";
import { queryKeys } from "@/queryKeys";
import { Borrower } from "@/types/borrower";

interface AssignUnderwriterDialogProps {
  open: boolean;
  borrower: Borrower | null;
  onClose: () => void;
}

const AssignUnderwriterDialog: React.FC<AssignUnderwriterDialogProps> = ({
  open,
  borrower,
  onClose,
}) => {
  const { data, isLoading } = useQuery({
    queryKey: queryKeys.underwriters(),
    queryFn: () => underwriterService.list(),
    enabled: open,
  });

  const underwriters = data?.results ?? [];

  const currentUnderwriterId = borrower?.underwriter_email
    ? underwriters.find((u) => u.email === borrower.underwriter_email)?.id
    : undefined;

  const [selectedId, setSelectedId] = useState<string | undefined>(undefined);

  // Sync selection to current underwriter when dialog opens
  React.useEffect(() => {
    if (open) {
      setSelectedId(currentUnderwriterId !== undefined ? String(currentUnderwriterId) : undefined);
    }
  }, [open, currentUnderwriterId]);

  const assignMutation = useAssignUnderwriter();

  const handleConfirm = () => {
    if (!borrower || !selectedId) return;
    assignMutation.mutate(
      { borrowerId: borrower.id, underwriterId: Number(selectedId) },
      { onSuccess: onClose }
    );
  };

  return (
    <Dialog open={open} onOpenChange={(isOpen) => !isOpen && onClose()}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Assign Underwriter</DialogTitle>
        </DialogHeader>

        <div className="space-y-4 py-2">
          {(borrower?.underwriter_name || borrower?.underwriter_email) && (
            <p className="rounded-md border border-border bg-muted/50 px-3 py-2 text-sm text-muted-foreground">
              Currently assigned to{" "}
              <span className="font-medium text-foreground">
                {borrower.underwriter_name || borrower.underwriter_email}
              </span>
              {" — "}selecting a new underwriter will replace them.
            </p>
          )}

          <Select
            value={selectedId}
            onValueChange={setSelectedId}
            disabled={isLoading || underwriters.length === 0}
          >
            <SelectTrigger>
              <SelectValue placeholder={isLoading ? "Loading…" : "Select an underwriter"} />
            </SelectTrigger>
            <SelectContent>
              {underwriters.map((u) => {
                const label =
                  [u.first_name, u.last_name].filter(Boolean).join(" ") || u.name || u.email;
                return (
                  <SelectItem key={u.id} value={String(u.id)}>
                    <span className="truncate">
                      {label}
                      {u.organization_names?.length ? ` — ${u.organization_names.join(", ")}` : ""}
                    </span>
                  </SelectItem>
                );
              })}
            </SelectContent>
          </Select>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={assignMutation.isPending}>
            Cancel
          </Button>
          <Button onClick={handleConfirm} disabled={!selectedId || assignMutation.isPending}>
            {assignMutation.isPending ? "Assigning…" : "Assign"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default AssignUnderwriterDialog;
